import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import { getCandidateInvite, startCandidateInterview, logoSrc } from "../api/client";
import { saveAuthToken } from "../../utils/cookies";

// ---------------------------------------------------------------------------
// Candidate PUBLIC invite page (/business/invite/:token).
//
// This is the ONLY business surface a candidate ever touches. It is public: the
// opaque invite token IS the credential (no login), so this route lives OUTSIDE
// the console's ProtectedRoute. Privacy is the whole point — a candidate sees
// their company's branding, the round they were invited to, and a deadline, and
// NEVER a score or report. The backend enforces the same (the report endpoint
// 404s for business sessions); this page simply never asks for one.
//
// Launch reuses the SHARED interview engine: we call the passwordless `start`
// endpoint, bridge the minted consumer token into the auth cookie/localStorage so
// the engine authenticates the candidate, then hand off to the normal
// pre-interview-check → live-interview-panel pipeline exactly as the institution
// assignment flow does. A localStorage marker lets the post-interview screen divert
// the candidate to a branded thank-you instead of the recruiter-only report.
// ---------------------------------------------------------------------------

const TYPE_LABEL = {
  dsa: "Data Structures & Algorithms",
  algorithms: "Data Structures & Algorithms",
  system_design: "System Design",
  behavioral: "Behavioral",
  hr: "HR Screen",
  resume: "Resume-based",
  frontend: "Frontend",
  backend: "Backend",
  fullstack: "Full-stack",
  sql: "SQL",
  devops: "DevOps",
  data_science: "Data Science",
  ml: "Machine Learning",
  aptitude: "Aptitude",
  coding: "Coding",
  mixed: "Mixed",
};

const prettyType = (t) => {
  if (!t) return "Interview";
  if (TYPE_LABEL[t]) return TYPE_LABEL[t];
  return String(t)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

// Full-screen branded shell shared by every state.
function Shell({ logoUrl, companyName, children }) {
  const logo = logoSrc(logoUrl);
  return (
    <div className="min-h-screen bg-background px-4 py-10 sm:py-16">
      <div className="mx-auto flex w-full max-w-lg flex-col items-center">
        {/* Company brand */}
        <div className="mb-8 flex flex-col items-center text-center">
          {logo ? (
            <div className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-border bg-card p-2">
              <img
                src={logo}
                alt={companyName ? `${companyName} logo` : "Company logo"}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Icon name="Building2" size={28} />
            </div>
          )}
          <p className="text-lg font-semibold text-foreground">
            {companyName || "Your interview"}
          </p>
        </div>

        {children}

        {/* Powered-by footer — the product mark, kept subtle under the co-brand. */}
        <p className="mt-8 text-xs text-muted-foreground">
          Powered by{" "}
          <span className="font-semibold">
            prepx<span className="text-primary">ai</span>
          </span>
        </p>
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      {children}
    </div>
  );
}

function MetaRow({ icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon name={icon} size={15} />
        {label}
      </span>
      <span className="text-right text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

// Centered status panel for terminal / informational states.
function StatusPanel({ icon, tone = "muted", title, message, children }) {
  const toneClasses = {
    success: "bg-success/15 text-success",
    warning: "bg-warning/15 text-warning",
    muted: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <Card>
      <div className="flex flex-col items-center text-center">
        <div
          className={
            "mb-4 flex h-14 w-14 items-center justify-center rounded-full " +
            (toneClasses[tone] || toneClasses.muted)
          }
        >
          <Icon name={icon} size={26} />
        </div>
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        {message && (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>
        )}
        {children}
      </div>
    </Card>
  );
}

export default function CandidateInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await getCandidateInvite(token);
      setInvite(data);
      setLoadError(null);
    } catch (err) {
      setInvite(null);
      setLoadError(err || { error: "This invite link is invalid or has expired." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const launch = useCallback(async () => {
    if (!invite) return;
    setLaunchError("");
    setLaunching(true);
    try {
      const res = await startCandidateInterview(token);
      if (!res?.session_id) {
        throw { error: "Could not start your interview. Please try again." };
      }
      if (!res?.token) {
        throw {
          error:
            "We couldn't establish a secure interview session. Please refresh and try again.",
        };
      }

      // Bridge the minted consumer JWT into the auth cookie + localStorage so the
      // shared interview engine (pre-check → live panel) authenticates this
      // candidate. getAuthToken() reads document.cookie then falls back to
      // localStorage; the server also set an HttpOnly cookie, but we rely on the
      // body token so this works across the console/candidate host boundary and in
      // local dev where a Secure cookie won't be stored over http.
      saveAuthToken(res.token);

      const sessionId = res.session_id;
      const companyLogo = logoSrc(invite.logoUrl);
      const sessionConfig = {
        company: invite.companyName || "General",
        role: invite.jobTitle || "Candidate",
        interviewType: invite.interviewType,
        type: invite.interviewType,
        duration: invite.durationMinutes,
        difficulty: invite.difficulty,
        persona: "friendly",
        // Coin-free: the org's wallet credit was reserved at invite time.
        coinsDeducted: 0,
        coinsPerMinute: 0,
        isPlanSession: false,
        isChallenge: false,
        // Business-hiring co-branding + post-interview divert signal. Parallels the
        // institution-assignment fields the pre-check / live-panel already read.
        isBusinessHiring: true,
        companyName: invite.companyName || "",
        companyLogoUrl: companyLogo,
        jobTitle: invite.jobTitle || "",
        roundName: invite.roundName || "",
      };

      // live-interview-panel reads currentInterviewSession as its config fallback.
      localStorage.setItem(
        "currentInterviewSession",
        JSON.stringify({ ...sessionConfig, startTime: new Date().toISOString(), sessionId })
      );

      // Marker that OUTLIVES the panel clearing currentInterviewSession on end:
      // the generating-result page reads it to divert the candidate to a branded
      // thank-you rather than the recruiter-only score report.
      try {
        localStorage.setItem(
          "prepxai_biz_candidate",
          JSON.stringify({
            token,
            companyName: invite.companyName || "",
            jobTitle: invite.jobTitle || "",
            companyLogoUrl: companyLogo,
          })
        );
      } catch {
        /* ignore storage failures */
      }

      navigate("/pre-interview-check", {
        state: { sessionId, config: sessionConfig, isChallenge: false },
      });
    } catch (err) {
      setLaunchError(err?.error || "Could not start your interview. Please try again.");
      setLaunching(false);
      // A stale 'ready' that just expired/completed elsewhere is the usual cause —
      // refetch so the page reflects the true state (no full-screen spinner).
      load();
    }
  }, [token, invite, navigate, load]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Icon name="Loader" size={28} className="animate-spin" />
          <p className="text-sm">Loading your invite…</p>
        </div>
      </div>
    );
  }

  // ── Unknown / expired token ──────────────────────────────────────────────────
  if (loadError) {
    return (
      <Shell companyName="">
        <StatusPanel
          icon="Unlink"
          tone="muted"
          title="This link isn't valid"
          message={
            loadError.notFound
              ? "This interview link is invalid or has expired. If you think this is a mistake, please contact the company that invited you."
              : loadError.error || "We couldn't load this invite. Please try again shortly."
          }
        />
      </Shell>
    );
  }

  const {
    companyName,
    logoUrl,
    jobTitle,
    candidateName,
    state,
    roundName,
    roundNumber,
    interviewType,
    difficulty,
    durationMinutes,
    deadline,
    maxAttempts,
    attemptsUsed,
    attemptsLeft,
    message,
  } = invite;

  const greeting = `Hi ${candidateName || "there"},`;
  const attemptLabel =
    maxAttempts && maxAttempts > 1
      ? `Attempt ${Math.min((attemptsUsed || 0) + 1, maxAttempts)} of ${maxAttempts}`
      : null;

  const RoundMeta = () => (
    <div className="mt-5 divide-y divide-border rounded-xl border border-border bg-background px-4">
      <MetaRow icon="ClipboardList" label="Interview" value={prettyType(interviewType)} />
      {durationMinutes ? (
        <MetaRow icon="Clock" label="Duration" value={`${durationMinutes} min`} />
      ) : null}
      {difficulty ? (
        <MetaRow icon="Gauge" label="Level" value={prettyType(difficulty)} />
      ) : null}
      {deadline ? <MetaRow icon="CalendarClock" label="Complete by" value={deadline} /> : null}
      {attemptLabel ? <MetaRow icon="RotateCcw" label="Attempts" value={attemptLabel} /> : null}
    </div>
  );

  // ── State machine ────────────────────────────────────────────────────────────
  const renderBody = () => {
    switch (state) {
      case "ready":
        return (
          <Card>
            <p className="text-sm text-muted-foreground">{greeting}</p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              You&apos;re invited to interview
              {jobTitle ? (
                <>
                  {" "}
                  for the <span className="text-primary">{jobTitle}</span> role
                </>
              ) : null}
              .
            </h1>
            {roundName ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {roundNumber ? `Round ${roundNumber}: ` : ""}
                {roundName}
              </p>
            ) : null}

            <RoundMeta />

            <div className="mt-5 rounded-xl bg-muted/50 p-4 text-sm leading-relaxed text-muted-foreground">
              This is an AI-led interview. Make sure you have a working microphone
              and a quiet space — we&apos;ll run a quick device check before you begin.
            </div>

            {launchError ? (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{launchError}</span>
              </div>
            ) : null}

            <Button
              className="mt-6"
              size="lg"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              loading={launching}
              onClick={launch}
            >
              {attemptsUsed && attemptsUsed > 0 ? "Start retake" : "Start interview"}
            </Button>
          </Card>
        );

      case "in_progress":
        return (
          <Card>
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon name="PlayCircle" size={26} />
              </div>
              <h1 className="text-xl font-semibold text-foreground">
                You have an interview in progress
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Pick up right where you left off. Your progress has been saved.
              </p>
            </div>
            <RoundMeta />
            {launchError ? (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{launchError}</span>
              </div>
            ) : null}
            <Button
              className="mt-6"
              size="lg"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              loading={launching}
              onClick={launch}
            >
              Resume interview
            </Button>
          </Card>
        );

      case "not_open":
        return (
          <StatusPanel
            icon="Clock"
            tone="primary"
            title="Your interview isn't open yet"
            message={
              deadline
                ? `This round hasn't opened yet. We'll email you as soon as it's ready — you'll have until ${deadline} to complete it.`
                : "This round hasn't opened yet. We'll email you as soon as it's ready to begin."
            }
          >
            {roundName ? (
              <p className="mt-4 rounded-lg bg-muted px-3 py-2 text-sm font-medium text-foreground">
                {roundNumber ? `Round ${roundNumber}: ` : ""}
                {roundName}
              </p>
            ) : null}
          </StatusPanel>
        );

      case "expired":
        return (
          <StatusPanel
            icon="CalendarX"
            tone="warning"
            title="This interview has closed"
            message="The deadline for this interview has passed, so it can no longer be started. If you believe this is a mistake, please reach out to the company that invited you."
          />
        );

      case "completed":
        return (
          <StatusPanel
            icon="CheckCircle2"
            tone="success"
            title="Your interview is complete"
            message={`Thanks for completing your interview${
              companyName ? ` with ${companyName}` : ""
            }. Their hiring team will review your responses and follow up with next steps — there's nothing more you need to do here.`}
          />
        );

      case "withdrawn":
        return (
          <StatusPanel
            icon="UserX"
            tone="muted"
            title="This application is no longer active"
            message="This interview link has been withdrawn. If you have questions, please contact the company that invited you."
          />
        );

      case "not_invited":
        return (
          <StatusPanel
            icon="MailCheck"
            tone="primary"
            title="You're on the list"
            message={`You've been added to ${
              companyName || "the company"
            }'s hiring process. We'll email you a link as soon as your interview is ready to begin.`}
          />
        );

      case "closed":
      default:
        return (
          <StatusPanel
            icon="Info"
            tone="muted"
            title="This interview isn't available"
            message={
              message ||
              "This interview link is no longer active. If you have questions, please contact the company that invited you."
            }
          />
        );
    }
  };

  return (
    <Shell logoUrl={logoUrl} companyName={companyName}>
      {renderBody()}
    </Shell>
  );
}
